import {Platform} from 'react-native';
import * as Crypto from 'expo-crypto';
import {
  paysafeAccountId, paysafeEnvironment, paysafeSingleUseTokenKey, paysafeTokenizeAmount,
  paysafeTokenizeTransactionType
} from '../constants';

/**
 * Paysafe hosted-field card tokenization for the withdrawal destination card (SMG-4057).
 *
 * <p>Shaped like finixTokenization.ts and for the same reason: Paysafe JS mounts its own
 * cross-origin iframes into host elements we render and tokenizes from inside them, so it must own
 * the inputs and the PAN never enters our origin (PS-19). The single-use token it returns is
 * exchanged server-side for a multi-use handle by {@code POST /v1/cards/paysafe/instrument}; the
 * resulting card is a Paysafe payout destination ({@code paysafe:<id>}) and nothing else.</p>
 *
 * <p>Customer-invisible (PS-17): every message this module can produce is generic. Paysafe's own
 * error text ({@code displayMessage} / {@code detailedMessage}) is never surfaced or logged — only
 * its numeric code, which carries no card data.</p>
 */

type PaysafeTokenizationErrorCode =
  | 'UNSUPPORTED_PLATFORM' | 'CONFIGURATION_ERROR' | 'SDK_LOAD_FAILED' | 'SDK_LOAD_TIMEOUT' | 'SDK_UNAVAILABLE'
  | 'FIELDS_NOT_MOUNTED' | 'SETUP_FAILED' | 'SETUP_TIMEOUT' | 'CARD_UNAVAILABLE' | 'TOKENIZATION_FAILED'
  | 'TOKENIZATION_TIMEOUT' | 'UNSUPPORTED_CARD_BRAND';

type PaysafeTokenResult = {token: string};

/** The cardholder's address as the app already holds it (the KYC profile on the user). */
export type PaysafeBillingAddress = {street?: string, city?: string, state?: string, country?: string, zip?: string};

/** DOM ids of the three elements Paysafe mounts its card number / expiry / CVV iframes into. */
export type PaysafeCardFieldHostIds = {cardNumber: string, expiryDate: string, cvv: string};

type PaysafePaymentMethods = {card?: {error?: unknown}};
export type PaysafeFieldsInstance = {
  show: () => Promise<PaysafePaymentMethods>,
  tokenize: (options: Record<string, unknown>) => Promise<{token?: unknown}>,
  /**
   * The brand Paysafe JS recognised in the number field. Read as either a name ('American Express') or a
   * Paysafe card-type code ('AM'), so the AMEX check holds whichever this SDK version returns.
   */
  getCardBrand?: () => unknown
};
type PaysafeFields = {
  setup?: (apiKey: string, options: Record<string, unknown>) => Promise<PaysafeFieldsInstance>
};
type PaysafeWindow = Window & {paysafe?: {fields?: PaysafeFields}};

const sdkScriptId = 'evenplay-hosted-card-fields-sdk';
// Paysafe serves this script only under `latest` (versioned paths redirect away), so it cannot be
// pinned; it is tracked for PCI change detection in docs/runbooks/paysafe-payouts.md ("PCI").
const sdkSrc = 'https://hosted.paysafe.com/js/v1/latest/paysafe.min.js';
const sdkLoadTimeoutMs = 15000;
// Covers setup() + show(). Paysafe applies its own initializationTimeout (5s default) inside
// setup; this is the backstop for a promise that never settles at all.
const setupTimeoutMs = 15000;
const tokenizationSafetyTimeoutMs = 20000;

const tokenizeTransactionTypes = ['VERIFICATION', 'PAYMENT', 'STANDALONE_CREDIT', 'ORIGINAL_CREDIT'];
const defaultTokenizeTransactionType = 'VERIFICATION';
const defaultTokenizeAmount = '100';

export const paysafeTokenizationPreparationErrorMessage =
  'Secure card entry could not be prepared. Please try again.';

const paysafeTokenizationFailedMessage = 'Unable to verify this card. Please check the details and try again.';

// SMG-4797: the payout account takes Visa and Mastercard only — no American Express.
const amexUnsupportedMessage =
  "American Express isn't supported for this payout method. Please use a Visa or Mastercard.";
const cardTypeUnsupportedMessage =
  "This card type isn't supported for this payout method. Please use a Visa or Mastercard.";
/** Paysafe's "card type for which the merchant account is not configured". */
const cardTypeNotConfiguredProviderCode = '3008';

const isPaysafeTokenizationSupported = () => Platform.OS === 'web';

let sdkLoadPromise: Promise<void> | undefined;

class PaysafeTokenizationError extends Error {
  constructor(
    public readonly code: PaysafeTokenizationErrorCode, message: string, public readonly providerCode?: string
  ) {
    super(message);
    this.name = 'PaysafeTokenizationError';
  }
}

const getErrorCode = (error: unknown) => {
  const code = (error as {code?: unknown})?.code;
  return typeof code === 'string' ? code : undefined;
};

/** Paysafe JS codes are numeric (9nnn from the SDK, others from the Payments API). Anything else is dropped. */
const getProviderCode = (error: unknown) => {
  const code = (error as {code?: unknown})?.code;
  const text = typeof code === 'string' || typeof code === 'number' ? String(code) : '';
  return /^\d{1,6}$/.test(text) ? text : undefined;
};

/** Codes only — Paysafe's messages can echo card data back and name the provider. */
export const buildSafePaysafeTokenizationErrorLog = (error: unknown) => {
  const providerCode = (error as {providerCode?: unknown})?.providerCode;
  const code = getErrorCode(error) || 'UNKNOWN';
  return typeof providerCode === 'string' ? {code, providerCode} : {code};
};

/** The customer-facing message when the card's brand is one the payout account cannot take, else undefined. */
export const getUnsupportedCardBrandMessage = (error: unknown) =>
  error instanceof PaysafeTokenizationError && error.code === 'UNSUPPORTED_CARD_BRAND' ? error.message : undefined;

/** The recognised brand, lower-cased letters only ('americanexpress', 'visa', …); '' when unknown. Never throws. */
const readCardBrand = (instance: PaysafeFieldsInstance) => {
  try {
    const brand = typeof instance.getCardBrand === 'function' ? instance.getCardBrand() : undefined;
    return typeof brand === 'string' ? brand.toLowerCase().replace(/[^a-z]/g, '') : '';
  } catch {
    return '';
  }
};

// Names and Paysafe's own card-type code (AM, as paygate's allowed-card-brands reads it).
const isAmericanExpress = (instance: PaysafeFieldsInstance) =>
  ['americanexpress', 'amex', 'am'].includes(readCardBrand(instance));

const readEnv = (value: unknown) => typeof value === 'string' ? value.trim() : '';

const isValidOptionalAccountId = (accountId: string) => !accountId || /^\d{1,15}$/.test(accountId);

const isValidTokenizeAmount = (amount: string) => /^\d{1,9}$/.test(amount) && Number(amount) >= 1;

// Same alpha-3 → alpha-2 folding paygate applies to the stored country before it reaches Paysafe.
const alpha2Countries: Record<string, string> = {USA: 'US', CAN: 'CA'};

/** The first line of a KYC street address, which can carry "street\ncity, ST, zip" (as paygate reads it). */
const firstLine = (value?: string) => readEnv(value).split(/\r?\n/)[0].trim().replace(/\s+/g, ' ');

/**
 * Paysafe's {@code customerDetails.billingDetails} for tokenize, or undefined. Country + zip are
 * required by Paysafe when the block is sent at all, so a missing or malformed one omits the whole
 * block — tokenizing exactly as before — rather than failing the tokenize on our data.
 *
 * <p>Street, city and state go along whenever the profile has them. Paysafe keeps this token's billing
 * details on the saved card — the server's own billing details on the card save are ignored — and a
 * US original credit to that card is refused 400 / 5068 without street, city and state (measured in
 * TEST, 2026-10-09). A value Paysafe would reject (too long, or a state that is not a 2-letter code)
 * is left out rather than failing the tokenize.</p>
 */
const buildBillingDetails = (address?: PaysafeBillingAddress) => {
  const rawCountry = readEnv(address?.country).toUpperCase();
  const country = alpha2Countries[rawCountry] || rawCountry;
  const zip = readEnv(address?.zip);
  if (!/^[A-Z]{2}$/.test(country) || !zip || zip.length > 10) return undefined;
  const street = firstLine(address?.street);
  const city = readEnv(address?.city).replace(/\s+/g, ' ');
  const state = readEnv(address?.state).toUpperCase();
  return {
    ...(street && street.length <= 50 ? {street} : {}),
    ...(city && city.length <= 40 ? {city} : {}),
    ...(/^[A-Z]{2}$/.test(state) ? {state} : {}),
    country, zip
  };
};

const getConfig = () => {
  const apiKey = readEnv(paysafeSingleUseTokenKey);
  const environment = readEnv(paysafeEnvironment);
  const accountId = readEnv(paysafeAccountId);
  const transactionType = readEnv(paysafeTokenizeTransactionType) || defaultTokenizeTransactionType;
  const amount = readEnv(paysafeTokenizeAmount) || defaultTokenizeAmount;

  // The public key belongs to one environment and Paysafe JS silently defaults an omitted
  // environment to LIVE, so the environment must be stated rather than defaulted. A malformed
  // optional override fails closed too: it would otherwise surface as an opaque tokenize error.
  const isValid = !!apiKey && (environment === 'TEST' || environment === 'LIVE')
    && isValidOptionalAccountId(accountId) && tokenizeTransactionTypes.includes(transactionType)
    && isValidTokenizeAmount(amount);
  if (!isValid) {
    throw new PaysafeTokenizationError('CONFIGURATION_ERROR', paysafeTokenizationPreparationErrorMessage);
  }
  return {
    apiKey, environment, accountId: accountId ? Number(accountId) : undefined, transactionType,
    amount: Number(amount)
  };
};

/** Can this build offer Paysafe card entry at all? The server decides whether it SHOULD (D6). */
export const isPaysafeConfigured = () => {
  if (!isPaysafeTokenizationSupported()) return false;
  try {
    getConfig();
    return true;
  } catch {
    return false;
  }
};

const getPaysafeFields = () => {
  const fields = (window as PaysafeWindow).paysafe?.fields;
  return typeof fields?.setup === 'function' ? fields as Required<PaysafeFields> : undefined;
};

const preparePaysafeTokenization = (): Promise<void> => {
  if (!isPaysafeTokenizationSupported() || typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new PaysafeTokenizationError('UNSUPPORTED_PLATFORM', 'Card entry is not available on this platform yet.'));
  }
  try {
    getConfig();
  } catch (error) {
    return Promise.reject(error);
  }

  if (getPaysafeFields()) return Promise.resolve();
  if (sdkLoadPromise) return sdkLoadPromise;

  const pending = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(sdkScriptId) as HTMLScriptElement | null;
    const script = existingScript || document.createElement('script');
    let settled = false;

    const cleanup = () => {
      clearTimeout(timeout);
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
    const fail = (error: PaysafeTokenizationError) => {
      if (settled) return;
      settled = true;
      cleanup();
      script.remove();
      reject(error);
    };
    const handleLoad = () => {
      if (settled) return;
      if (!getPaysafeFields()) {
        fail(new PaysafeTokenizationError('SDK_UNAVAILABLE', paysafeTokenizationPreparationErrorMessage));
        return;
      }
      settled = true;
      cleanup();
      resolve();
    };
    const handleError = () => fail(new PaysafeTokenizationError('SDK_LOAD_FAILED', paysafeTokenizationPreparationErrorMessage));
    const timeout = setTimeout(
      () => fail(new PaysafeTokenizationError('SDK_LOAD_TIMEOUT', paysafeTokenizationPreparationErrorMessage)),
      sdkLoadTimeoutMs
    );

    script.addEventListener('load', handleLoad, {once: true});
    script.addEventListener('error', handleError, {once: true});

    if (!existingScript) {
      script.id = sdkScriptId;
      script.async = true;
      script.src = sdkSrc;
      document.head.appendChild(script);
    }
  });

  sdkLoadPromise = pending;
  void pending.catch(() => {
    if (sdkLoadPromise === pending) sdkLoadPromise = undefined;
  });
  return pending;
};

const rejectAfter = (timeoutMs: number, code: PaysafeTokenizationErrorCode) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const promise = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new PaysafeTokenizationError(code, paysafeTokenizationPreparationErrorMessage)), timeoutMs);
  });
  return {promise, cancel: () => clearTimeout(timer)};
};

/**
 * Mount Paysafe's hosted card number / expiry / CVV fields into existing elements, returning the
 * instance to tokenize with later plus an unmount. The hosts must already be in the DOM — Paysafe
 * resolves them by CSS selector.
 */
export const mountPaysafeCardFields = async (
  hostIds: PaysafeCardFieldHostIds, style?: Record<string, unknown>
) => {
  await preparePaysafeTokenization();
  const fields = getPaysafeFields();
  if (!fields) {
    throw new PaysafeTokenizationError('SDK_UNAVAILABLE', paysafeTokenizationPreparationErrorMessage);
  }
  const ids = [hostIds.cardNumber, hostIds.expiryDate, hostIds.cvv];
  if (typeof document === 'undefined' || ids.some(id => !document.getElementById(id))) {
    throw new PaysafeTokenizationError('FIELDS_NOT_MOUNTED', paysafeTokenizationPreparationErrorMessage);
  }

  const {apiKey, environment, accountId} = getConfig();
  // replaceChildren(), not innerHTML: this detaches Paysafe's iframes, it does not render markup.
  const unmount = () => ids.forEach(id => document.getElementById(id)?.replaceChildren());
  // Start from empty hosts: an earlier attempt that timed out can still finish late and leave its
  // iframes behind, and a second set would stack under the first.
  unmount();

  const timeout = rejectAfter(setupTimeoutMs, 'SETUP_TIMEOUT');
  try {
    const setupAndShow = async () => {
      const instance = await fields.setup(apiKey, {
        currencyCode: 'USD', environment,
        ...(accountId ? {accounts: {default: accountId}} : {}),
        ...(style ? {style} : {}),
        fields: {
          // Placeholders match the eProtect entry form so the two read the same to the customer.
          cardNumber: {selector: `#${hostIds.cardNumber}`, placeholder: '0000 0000 0000 0000', separator: ' '},
          expiryDate: {selector: `#${hostIds.expiryDate}`, placeholder: 'MM/YY'},
          cvv: {selector: `#${hostIds.cvv}`, placeholder: '123', optional: false}
        }
      });
      return {instance, paymentMethods: await instance.show()};
    };
    const {instance, paymentMethods} = await Promise.race([setupAndShow(), timeout.promise]);

    // Setup succeeding is not enough: card is the one payment method we need, and Paysafe reports
    // a per-method failure (e.g. card not enabled on the account) here rather than rejecting.
    if (!paymentMethods?.card || paymentMethods.card.error) {
      throw new PaysafeTokenizationError('CARD_UNAVAILABLE', paysafeTokenizationPreparationErrorMessage);
    }
    return {instance, unmount};
  } catch (error) {
    unmount();
    if (error instanceof PaysafeTokenizationError) throw error;
    throw new PaysafeTokenizationError(
      'SETUP_FAILED', paysafeTokenizationPreparationErrorMessage, getProviderCode(error)
    );
  } finally {
    timeout.cancel();
  }
};

/**
 * Tokenize the card entered in a mounted instance and resolve the single-use payment token.
 *
 * <p>Settles once, behind a safety timeout, matching the Finix and eProtect wrappers next door: a
 * tokenize promise whose iframe is torn down mid-flight may never settle. Transaction type and
 * amount are env-overridable because the sandbox has to confirm which a card save accepts. The
 * billing country + zip are sent when the app already has them (AVS on the verification).</p>
 *
 * <p>An American Express is refused before tokenizing (SMG-4797): the payout account takes none, and
 * Paysafe would only refuse it at tokenize. That refusal (3008) is mapped to the same kind of message
 * for any brand the account is not configured for — Discover or JCB, or an AMEX whose brand Paysafe JS
 * had not reported yet. Both reject with {@code UNSUPPORTED_CARD_BRAND};
 * {@link getUnsupportedCardBrandMessage} reads the message.</p>
 */
export const tokenizePaysafeCard = (
  instance: PaysafeFieldsInstance, billingAddress?: PaysafeBillingAddress
): Promise<PaysafeTokenResult> => {
  const {transactionType, amount} = getConfig();
  if (isAmericanExpress(instance)) {
    return Promise.reject(new PaysafeTokenizationError('UNSUPPORTED_CARD_BRAND', amexUnsupportedMessage));
  }
  const billingDetails = buildBillingDetails(billingAddress);

  return new Promise<PaysafeTokenResult>((resolve, reject) => {
    let settled = false;
    let safetyTimeout: ReturnType<typeof setTimeout>;

    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(safetyTimeout);
      callback();
    };
    const failTokenization = (error?: unknown) => finish(() => {
      const providerCode = getProviderCode(error);
      reject(providerCode === cardTypeNotConfiguredProviderCode
        ? new PaysafeTokenizationError(
          'UNSUPPORTED_CARD_BRAND',
          isAmericanExpress(instance) ? amexUnsupportedMessage : cardTypeUnsupportedMessage, providerCode
        )
        : new PaysafeTokenizationError('TOKENIZATION_FAILED', paysafeTokenizationFailedMessage, providerCode));
    });

    safetyTimeout = setTimeout(
      () => finish(() => reject(new PaysafeTokenizationError(
        'TOKENIZATION_TIMEOUT',
        'We are experiencing technical difficulties. Please try again later or contact support. (timeout)'
      ))),
      tokenizationSafetyTimeoutMs
    );

    try {
      instance.tokenize({
        amount, transactionType, paymentType: 'CARD', merchantRefNum: Crypto.randomUUID(),
        ...(billingDetails ? {customerDetails: {billingDetails}} : {})
      }).then(result => {
        const token = result?.token;
        if (typeof token !== 'string' || !token) {
          failTokenization();
          return;
        }
        finish(() => resolve({token}));
      }, failTokenization);
    } catch (error) {
      failTokenization(error);
    }
  });
};
